import type { LiveRateSnapshot, SessionRecord } from '@/domain/types'
import type { AppLocale } from '@/i18n/messages'
import { compact, signedCompact } from './widget-format'

export interface ExpReadoutPresentation { label: string; description: string }

export function presentExpReadout(rates: LiveRateSnapshot | null, session: SessionRecord | null, locale: AppLocale): ExpReadoutPresentation {
  const en = locale === 'en'
  const readout = rates?.expReadout
  const calculation = rates?.expPerHour.calculationStatus
  const pending = calculation === 'pending-transition' || calculation === 'unresolved-level'
  const status = readout?.status ?? (pending ? 'stale' : 'ready')
  const recovery = readout?.scope === 'recovery'
  const ended = session?.status === 'ended'
  // Brief confirmation is silent; expired readings and recovery scope stay explicit.
  const labels = en ? { ready: '', updating: '', stale: 'Waiting for readings', recovering: 'Gathering samples' }
    : { ready: '', updating: '', stale: '等待读数', recovering: '积累样本' }
  let label = labels[status]
  if (ended && recovery && status === 'recovering') label = en ? 'No rate available' : '暂无统计'
  else if (recovery && status !== 'recovering') label = `${en ? 'Since recovery' : '恢复后效率'}${label ? ` · ${label}` : ''}`
  const reasons = en ? {
    'checking-change': 'Confirming an EXP change.', 'waiting-level': 'Waiting for a fresh level reading.',
    'missing-levels': 'Cross-level EXP evidence is missing.', 'source-unreadable': 'Waiting for a readable game frame.',
    'confirmation-timeout': 'The EXP change could not be confirmed in time.', 'continuity-break': 'Capture continuity was interrupted during confirmation.',
  } : {
    'checking-change': '正在核对经验变化。', 'waiting-level': '等待等级读数。', 'missing-levels': '跨级经验缺失。',
    'source-unreadable': '画面暂不可读，等待新的清晰读数。', 'confirmation-timeout': '经验变化未能在确认时间内完成核对。',
    'continuity-break': '确认期间采集连续性中断。',
  }
  const lines = [en ? 'EXP / hour' : '经验 / 小时']
  if (readout?.reason) lines.push(reasons[readout.reason])
  else if (pending) lines.push(en ? 'Waiting for complete EXP evidence.' : '等待完整的经验确认信息。')
  if (readout?.incomplete) lines.push(en
    ? 'Some EXP readings could not be confirmed, so session net EXP is unavailable. The rate covers only the uninterrupted segment after recovery.'
    : '有一段经验未能确认，因此暂不显示本场净经验。恢复后效率只统计恢复后的连续区间。')
  const time = (at: number) => Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(at)
  if (rates?.expRecovery?.startedAt != null) lines.push(`${en ? 'Recovery baseline' : '恢复起点'}：${time(rates.expRecovery.startedAt)}`)
  const last = readout?.lastConfirmed
  if (last) lines.push(`${en ? 'Last confirmed' : '上次确认'}：${time(last.sourceAt)}\n${compact(last.value, locale)} ${en ? 'EXP/h' : '经验/小时'} · ${en ? 'Net EXP' : '净经验'} ${signedCompact(last.netGain, locale)}`)
  if (status === 'updating') lines.push(en ? 'Showing the last confirmed values while checking.' : '校验期间显示上次确认值，尚未计入待确认经验。')
  if (status === 'stale') lines.push(en ? 'The last reading is too old to show as a current rate.' : '上次读数已过期，暂不作为当前效率显示。')
  return { label, description: lines.join('\n') }
}
